/**
 * Executing a read plan: turning ranges into bytes.
 *
 * Separate from `read-plan.ts` (which decides *what* to read) and from the
 * worker (which decides what the numbers mean) so each can be tested without
 * the others. This module is the only one that turns a plan into traffic.
 *
 * Two flavours of every read, and the distinction is load-bearing:
 *
 *  - the `…AtRuns` functions read a tensor at **row runs handed to them**;
 *  - `fetchTensorBytes` / `fetchAffineTriple` plan the runs first, then call
 *    those.
 *
 * Single-tensor analysis wants the second: `planTensorRead` seeds its PRNG from
 * `(sha, tensorName)`, so a deep link pinned to a commit always samples the same
 * rows. **Compare mode wants the first**, and cannot use the second. A and B sit
 * at different commits, so planning each side independently would sample
 * *different rows* of the two tensors — and comparing row 40 of a base model
 * against row 9000 of its finetune element-wise yields a cosine, a relF and a
 * delta histogram that are all perfectly well-formed and completely meaningless.
 * B is therefore read at A's runs, which is the only arrangement in which "the
 * same tensor" is a claim about the same weights.
 */

import { GGML_BLOCK_BYTES } from "../analysis/ggml-dequant.js";
import type { InventoryTensor } from "../safetensors/inventory.js";
import { type ResolvedRepo, fetchRange, fileUrl } from "./hub.js";
import {
  type ByteRange,
  type ReadPlan,
  type RowRun,
  planTensorRead,
} from "./read-plan.js";

/** Cooperative cancellation flag — see `checkAborted`. */
export interface AbortFlag {
  readonly aborted: boolean;
}

export interface TensorBytes {
  /** Concatenated payload, in file order. */
  readonly bytes: ArrayBuffer;
  readonly plan: ReadPlan;
}

/**
 * Fetch the bytes a plan describes.
 *
 * Ranges are issued concurrently and reassembled in plan order — `hub.ts`'s
 * semaphore caps how many are actually in flight, so handing it 32 promises
 * queues them rather than stampeding the Hub. Order matters on reassembly: the
 * rows must land contiguously or every row-based statistic downstream silently
 * measures a shuffled matrix.
 */
export async function fetchTensorBytes(
  repo: ResolvedRepo,
  tensor: InventoryTensor,
  signal?: AbortFlag,
): Promise<TensorBytes> {
  const plan = planTensorRead(tensor, repo.sha);
  const bytes = await fetchTensorBytesAtRuns(repo, tensor, plan.rowRuns, signal);
  return { bytes, plan };
}

/**
 * Fetch one plain tensor at row runs chosen elsewhere — compare mode's B side.
 *
 * The runs are interpreted at *this* tensor's row width, not the width they were
 * planned against, so a bf16 base and an f32 finetune of the same shape read the
 * same logical rows despite different byte arithmetic. What the runs must agree
 * on is the row *count*; a run that reaches past the end of this tensor is
 * refused rather than short-read (see `rowRunRanges`).
 */
export async function fetchTensorBytesAtRuns(
  repo: ResolvedRepo,
  tensor: InventoryTensor,
  runs: readonly RowRun[],
  signal?: AbortFlag,
): Promise<ArrayBuffer> {
  const bytes = await concatRanges(
    fileUrl(repo, tensor.file),
    rowRunRanges(tensor, runs),
  );
  checkAborted(signal);
  return bytes;
}

/**
 * Fetch a quantized triple — packed weight plus its scale and bias planes.
 *
 * The three tensors have different row *widths* but the same row *count*, and
 * they must be sampled consistently: a scale plane read from different rows
 * than the weights it scales produces a dequantized tensor that is wrong
 * everywhere while looking entirely plausible. So the plan is made once, on the
 * packed weight, and the sibling planes are read at the same **row runs** —
 * which is why `ReadPlan` carries rows and not only bytes.
 *
 * The planes are tiny relative to the weight (one value per group, so 1/32 to
 * 1/128 of the element count at f16), which is what makes fetching all three
 * affordable.
 */
export interface AffineTripleBytes {
  readonly packed: ArrayBuffer;
  readonly scales: ArrayBuffer;
  readonly biases: ArrayBuffer;
  readonly plan: ReadPlan;
  /** Rows actually delivered — the sum of the plan's row runs. */
  readonly rows: number;
}

export async function fetchAffineTriple(
  repo: ResolvedRepo,
  weight: InventoryTensor,
  scales: InventoryTensor,
  biases: InventoryTensor,
  signal?: AbortFlag,
): Promise<AffineTripleBytes> {
  const plan = planTensorRead(weight, repo.sha);
  const triple = await fetchAffineTripleAtRuns(
    repo,
    weight,
    scales,
    biases,
    plan.rowRuns,
    signal,
  );
  return { ...triple, plan };
}

/** An affine triple read at row runs chosen elsewhere; everything but the plan. */
export type AffineTripleAtRuns = Omit<AffineTripleBytes, "plan">;

/**
 * Fetch a quantized triple at row runs chosen elsewhere — compare mode's B side
 * when B is the quantization of A.
 *
 * All three tensors are read at the *same* runs for the same reason
 * `fetchAffineTriple` does it: the planes must describe the rows the packed
 * weights came from. The runs merely arrive from outside instead of from a plan
 * built here.
 */
export async function fetchAffineTripleAtRuns(
  repo: ResolvedRepo,
  weight: InventoryTensor,
  scales: InventoryTensor,
  biases: InventoryTensor,
  runs: readonly RowRun[],
  signal?: AbortFlag,
): Promise<AffineTripleAtRuns> {
  // Every range is derived before the first request, so a run that overruns any
  // of the three tensors fails before a single byte is fetched rather than after
  // two thirds of the traffic has already been spent.
  const weightRanges = rowRunRanges(weight, runs);
  const scaleRanges = rowRunRanges(scales, runs);
  const biasRanges = rowRunRanges(biases, runs);

  // Sequential, not `Promise.all`: the concurrency cap would permit four at
  // once, but the no-crawling rule is about what we *choose* to ask for, and
  // the planes are a rounding error next to the weight anyway.
  const packed = await concatRanges(fileUrl(repo, weight.file), weightRanges);
  const scalesBytes = await concatRanges(fileUrl(repo, scales.file), scaleRanges);
  const biasesBytes = await concatRanges(fileUrl(repo, biases.file), biasRanges);
  checkAborted(signal);

  const rows = runs.reduce((sum, run) => sum + run.rowCount, 0);
  return { packed, scales: scalesBytes, biases: biasesBytes, rows };
}

/**
 * Byte ranges for the given row runs within one tensor, at its own row width.
 *
 * The row width comes from `byteLength / rows` rather than from the dtype, which
 * is what makes this correct for a plane of a *different* width — a scales plane,
 * a packed u32 weight, an f32 finetune of a bf16 base — without knowing anything
 * about how its elements are encoded.
 *
 * **A run that reaches past the end is an error, not a short read.** This is the
 * whole risk of applying A's runs to B: if B has fewer rows, clamping the run
 * would hand back a buffer whose rows no longer line up with A's from that run
 * onward, and every element-wise statistic after the truncation point would be
 * comparing unrelated weights while reporting a confident number. Two tensors of
 * the same name whose row counts differ are a lineage mismatch, and the only
 * honest answer is to say so.
 */
function rowRunRanges(
  tensor: InventoryTensor,
  runs: readonly RowRun[],
): readonly ByteRange[] {
  const rows = tensor.shape.slice(0, -1).reduce((a, b) => a * b, 1) || 1;
  const rowBytes = tensor.byteLength / Math.max(1, rows);
  if (!Number.isInteger(rowBytes)) {
    throw new Error(
      `${tensor.name} is ${tensor.byteLength} bytes over ${rows} rows, which is not a whole number of bytes per row — it cannot be sliced into rows at all`,
    );
  }

  /*
   * Block formats: a row must be a whole number of blocks, and this checks it
   * rather than trusting it.
   *
   * ggml requires the contiguous axis to divide by the block size, so a
   * row-aligned slice of a `Q8_0` tensor is already block-aligned — the
   * alignment is a consequence of the format, not something this planner
   * arranges. That makes it exactly the kind of invariant worth asserting where
   * it is relied on: the reviewer's ruling on the packed-int alignment was that
   * an invariant maintained only in the producer is a trap for the next caller,
   * and this is the same shape (2026-08-09).
   *
   * A slice starting mid-block would hand the decoder bytes whose first two are
   * a quant rather than a scale — it would decode, and every value would be
   * wrong.
   */
  const blockBytes = GGML_BLOCK_BYTES.get(tensor.dtype);
  if (blockBytes !== undefined && rowBytes % blockBytes !== 0) {
    throw new Error(
      `${tensor.name} has ${rowBytes} bytes per row, which is not a whole number of ${blockBytes}-byte ${tensor.dtype} blocks. A row run would start mid-block, where the first bytes are quants rather than a scale — every value would decode wrongly rather than fail.`,
    );
  }

  return runs.map((run) => {
    const endRow = run.startRow + run.rowCount;
    if (run.startRow < 0 || endRow > rows) {
      throw new Error(
        `Row run [${run.startRow}, ${endRow}) does not fit ${tensor.name}, which has ${rows} rows. Reading it short would silently misalign every row after it against the tensor it is being compared with — matched-name tensors whose row counts differ are a lineage mismatch, not something to truncate.`,
      );
    }
    return {
      start: tensor.absoluteStart + run.startRow * rowBytes,
      end: tensor.absoluteStart + endRow * rowBytes,
    };
  });
}

/**
 * Cheap cooperative cancellation: the visitor clicked another tensor while this
 * one was in flight, so the result is already stale. The requests themselves are
 * not aborted — they are small, mostly already complete by the time this is
 * checked, and an AbortController per range would complicate the semaphore for
 * no real saving.
 */
function checkAborted(signal: AbortFlag | undefined): void {
  if (signal?.aborted) {
    throw new DOMException("Superseded by a newer selection", "AbortError");
  }
}

async function concatRanges(
  url: string,
  ranges: readonly ByteRange[],
): Promise<ArrayBuffer> {
  const chunks = await Promise.all(
    ranges.map((range) => fetchRange(url, range.start, range.end)),
  );
  const total = chunks.reduce((sum, chunk) => sum + chunk.byteLength, 0);
  const bytes = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    bytes.set(new Uint8Array(chunk), offset);
    offset += chunk.byteLength;
  }
  return bytes.buffer;
}

/** An AWQ/GPTQ group read for analysis: sampled payload, whole planes. */
export interface PackedGroupBytes {
  readonly payload: ArrayBuffer;
  readonly qzeros: ArrayBuffer;
  readonly scales: ArrayBuffer;
  readonly gIdx: ArrayBuffer | undefined;
  /** Input features each payload run covers, in payload order. */
  readonly runs: readonly {
    readonly firstFeature: number;
    readonly features: number;
  }[];
  readonly plan: ReadPlan;
}

/**
 * Fetch an AWQ/GPTQ group.
 *
 * Unlike `fetchAffineTriple`, the planes are **not** read at the payload's row
 * runs. MLX's scale and bias planes are indexed by row, so the same runs select
 * the same rows in all three tensors; these planes are indexed by *group*, and
 * under GPTQ's act-order a sampled row's group can be anywhere in the plane.
 * Slicing them to the payload's rows would fetch the wrong numbers.
 *
 * So the planes come whole. They are around a thirty-second of the payload —
 * one scale per `groupSize` weights at 16 bits, against 4 bits per weight — so
 * this costs less than a second sampled read would.
 */
export async function fetchPackedGroup(
  repo: ResolvedRepo,
  qweight: InventoryTensor,
  qzeros: InventoryTensor,
  scales: InventoryTensor,
  gIdx: InventoryTensor | undefined,
  axis: "columns" | "rows",
  bits: number,
  signal?: AbortFlag,
): Promise<PackedGroupBytes> {
  const plan = planTensorRead(qweight, repo.sha);
  const payloadRanges = rowRunRanges(qweight, plan.rowRuns);

  const payload = await concatRanges(fileUrl(repo, qweight.file), payloadRanges);
  const qzerosBytes = await fetchWhole(repo, qzeros);
  const scalesBytes = await fetchWhole(repo, scales);
  const gIdxBytes = gIdx ? await fetchWhole(repo, gIdx) : undefined;
  checkAborted(signal);

  // A payload row is one input feature when columns are packed, and
  // `32 / bits` of them when rows are.
  const perWord = 32 / bits;
  const runs = plan.rowRuns.map((run) =>
    axis === "columns"
      ? { firstFeature: run.startRow, features: run.rowCount }
      : { firstFeature: run.startRow * perWord, features: run.rowCount * perWord },
  );

  return {
    payload,
    qzeros: qzerosBytes,
    scales: scalesBytes,
    gIdx: gIdxBytes,
    runs,
    plan,
  };
}

/** One tensor, whole. Used only for the small per-group planes. */
async function fetchWhole(
  repo: ResolvedRepo,
  tensor: InventoryTensor,
): Promise<ArrayBuffer> {
  return concatRanges(fileUrl(repo, tensor.file), [
    { start: tensor.absoluteStart, end: tensor.absoluteEnd },
  ]);
}
